import React, {
   useEffect,
   useMemo,
   useRef,
   useState,
   useCallback,
   useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";

import {
   subscribePopup,
   getCurrentPopup,
   closePopup as closePopupStore,
   getCurrentSubPopup,
   closeSubPopup as requestCloseSubPopup,
} from "../Common/popupUIStore";
import { triggerHapticFeedback } from "../../utils/hapticFeedback";

import { ReactSVG } from "react-svg";
import addIcon from "../../assets/svg/add-s.svg";
import StudentInfo from "../Popups/StudentInfo";
import EventInfoPopup from "../Popups/EventInfo";
import StudentSelfProfilePopup from "../Popups/StudentSelfProfilePopup";
import StudentReservationsPopup from "../Popups/StudentReservationsPopup";
import InstructorProfileUI from "../Popups/InstructorProfileUI";
import ManagerAdminProfileUI from "../Popups/ManagerAdminProfileUI";
import ProfessorProfileUI from "../Popups/ProfessorProfileUI";
import PersonalCreateUI from "../Popups/PersonalCreateUI";
import SAddProgAutoAssign from "../Popups/SAddProgAutoAssign";
import StudentVideoLibraryPopup from "../Popups/StudentVideoLibraryPopup";
import QuestionCategoriesPopup from "../Popups/QuestionCategories";
import StudentsMultiSelectPopup from "../Popups/StudentsMultiSelectPopup";
import EditReservation from "../Popups/EditReservation/EditReservation";
import CreateRezervation from "../Popups/CreateRezervation/CreateRezervation";
import UltraSlotHistoryPopup from "../Popups/UltraSlotHistoryPopup";
import InstructorStudentsPopup from "../Popups/InstructorStudentsPopup";
import PracticalExamSlotPopup from "../Popups/PracticalExamSlotPopup";
import PracticalExamGenerateSlotsPopup from "../Popups/PracticalExamGenerateSlotsPopup";
import PracticalExamBookStudentPopup from "../Popups/PracticalExamBookStudentPopup";
import InstructorSubstitutionsPopup from "../Popups/InstructorSubstitutionsPopup";
import InstructorOrderEditorPopup from "../Popups/InstructorOrderEditorPopup";
import StudentMandatoryRatingsPopup from "../Popups/StudentMandatoryRatingsPopup";
import AdminReviewsPopup from "../Popups/AdminReviewsPopup";
import NotificationsPopup from "../Popups/NotificationsPopup";
import AppearanceSettingsPopup from "../Popups/AppearanceSettingsPopup";
import HomeWidgetGalleryPopup from "../Popups/HomeWidgetGalleryPopup";
import StaffChatPopup from "../Popups/StaffChatPopup";
import StudentEnrollPopup from "../Popups/StudentEnrollPopup";
import GroupEditPopup from "../Popups/GroupEditPopup";
import SendRecommendedVideoPopup from "../Popups/SendRecommendedVideoPopup";
import PracticalExamBooking from "../SPanel/PracticalExamBooking";

// ține-l egal cu durata din CSS
const ANIM_MS = 300;

// aici pui tipurile unde vrei “fără animație de conținut” (dar panel-ul rămâne animat)
const NO_CONTENT_MOTION_TYPES = new Set([
   "staffDetails",
   "managerAdminDetails",
   "professorDetails",
   "personalCreate",
   "studentEnroll",
]);

const POPUP_UI_IS_LOW_SPEC_DEVICE =
   typeof navigator !== "undefined" &&
   ((Number(navigator.deviceMemory) > 0 &&
      Number(navigator.deviceMemory) <= 4) ||
      (Number(navigator.hardwareConcurrency) > 0 &&
         Number(navigator.hardwareConcurrency) <= 4));

// ✅ marker pt history state
const POPUP_HISTORY_MARK = "__popup_ui_guard__";

function isMobilePopupViewport() {
   return (
      typeof window !== "undefined" &&
      window.matchMedia?.("(max-width: 860px)").matches
   );
}

function buildPopupHistoryState(extra, mode = "push") {
   if (typeof window === "undefined") return { ...extra };

   const currentState = window.history.state || {};
   const nextState = { ...currentState, ...extra };

   if (mode === "push") {
      const currentIdx = Number(currentState?.idx);
      if (Number.isFinite(currentIdx)) {
         nextState.idx = currentIdx + 1;
      }

      const baseKey = String(currentState?.key || "popup");
      nextState.key = `${baseKey}-popup-${Date.now()}`;
   }

   return nextState;
}

function resolvePopupUIPortalHost() {
   if (typeof document === "undefined") return null;
   const doc = document;
   const fullscreenEl = doc.fullscreenElement || doc.webkitFullscreenElement;
   if (
      !fullscreenEl ||
      fullscreenEl === doc.documentElement ||
      fullscreenEl === doc.body
   ) {
      return doc.body;
   }
   return fullscreenEl;
}

function isIOSLikeDevice() {
   if (typeof navigator === "undefined") return false;
   const ua = String(navigator.userAgent || "");
   const platform = String(navigator.platform || "");
   const maxTouchPoints = Number(navigator.maxTouchPoints || 0);
   return /iPad|iPhone|iPod/i.test(ua) ||
      (platform === "MacIntel" && maxTouchPoints > 1);
}

function shouldDisablePopupMotion(popupLike) {
   if (!popupLike) return false;
   const type = String(popupLike?.type || "").trim();
   const props = popupLike?.props || {};

   if (props?.popupMotion === "none" || props?.noAnimation === true) return true;
   if (typeof window !== "undefined") {
      if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
         return true;
      }

      const source = String(props?.openSource || props?.source || "")
         .trim()
         .toLowerCase();
      if (source === "calendar") return true;

      if (
         type === "createRezervation" &&
         window.matchMedia?.("(max-width: 860px)").matches &&
         (POPUP_UI_IS_LOW_SPEC_DEVICE || isIOSLikeDevice())
      ) {
         return true;
      }
   }

   return false;
}

function isMandatoryPopupType(type) {
   return String(type || "").trim() === "studentMandatoryRatings";
}

export default function PopupUI() {
   const panelRef = useRef(null);
   const [portalHost, setPortalHost] = useState(() => resolvePopupUIPortalHost());

   const raf1Ref = useRef(null);
   const raf2Ref = useRef(null);
   const clearContentTimerRef = useRef(null);
   const closeInterceptorRef = useRef(null);

   const seqRef = useRef(0); // anti-race: open/close rapid
   const motionOffRef = useRef(false);
   const ignorePopUntilTsRef = useRef(0);
   const popupOpenedAtTsRef = useRef(0);

   // ✅ history refs
   const backArmedRef = useRef(false);
   const ignoreNextPopRef = useRef(false);
   const popupHistoryIdRef = useRef(null);

   // ✅ store snapshot stabil (nu ratează primul click)
   const storePopup = useSyncExternalStore(
      subscribePopup,
      getCurrentPopup,
      getCurrentPopup,
   );

   // panel state (container rămâne montat mereu)
   const [isOpen, setIsOpen] = useState(false);

   // conținutul curent (îl păstrăm în timpul “closing”)
   const [shownPopup, setShownPopup] = useState(null);

   const clearTimers = useCallback(() => {
      if (raf1Ref.current) cancelAnimationFrame(raf1Ref.current);
      if (raf2Ref.current) cancelAnimationFrame(raf2Ref.current);
      raf1Ref.current = null;
      raf2Ref.current = null;

      if (clearContentTimerRef.current)
         clearTimeout(clearContentTimerRef.current);
      clearContentTimerRef.current = null;
   }, []);

   const scheduleClearContent = useCallback((seq) => {
      clearContentTimerRef.current = setTimeout(() => {
         if (seqRef.current !== seq) return;
         if (getCurrentPopup()) return;
         setShownPopup(null);
      }, ANIM_MS + 60);
   }, []);

   const confirmPopupClose = useCallback((type, options = {}) => {
      if (isMandatoryPopupType(type)) return false;
      if (options?.skipConfirm) return true;
      if (type !== "sAddProg") return true;
      if (typeof closeInterceptorRef.current === "function") {
         // Interceptorul poate bloca închiderea (return false) ca să arate
         // confirmarea de pierdere progres; altfel lasă închiderea să continue.
         return closeInterceptorRef.current() !== false;
      }
      return true;
   }, []);

   const registerCloseInterceptor = useCallback((handler) => {
      closeInterceptorRef.current =
         typeof handler === "function" ? handler : null;

      return () => {
         if (closeInterceptorRef.current === handler) {
            closeInterceptorRef.current = null;
         }
      };
   }, []);

   // ✅ arm back-guard (pushState) când deschizi popup
   const armBackGuard = useCallback((popupId) => {
      if (typeof window === "undefined") return;
      if (!isMobilePopupViewport()) return;

      const nextPopupId = String(popupId ?? "").trim();
      if (!nextPopupId) return;

      const marker = {
         [POPUP_HISTORY_MARK]: true,
         popupHistoryId: nextPopupId,
         t: Date.now(),
      };
      const currentState = window.history.state || {};

      if (
         backArmedRef.current &&
         currentState?.[POPUP_HISTORY_MARK] &&
         String(currentState?.popupHistoryId ?? "") === nextPopupId
      ) {
         return;
      }

      try {
         // dacă am rămas deja pe un marker, îl actualizăm în loc să mai adăugăm încă unul
         if (currentState?.[POPUP_HISTORY_MARK]) {
            window.history.replaceState(
               buildPopupHistoryState(marker, "replace"),
               "",
               window.location.href,
            );
         } else {
            window.history.pushState(
               buildPopupHistoryState(marker, "push"),
               "",
               window.location.href,
            );
         }
         backArmedRef.current = true;
         popupHistoryIdRef.current = nextPopupId;
      } catch {
         // ignore
      }
   }, []);

   // ✅ la “back gesture / back button” închide popup-ul, nu naviga
   useEffect(() => {
      if (typeof window === "undefined") return;

      const onPopState = () => {
         if (!isMobilePopupViewport()) return;
         if (Date.now() < Number(ignorePopUntilTsRef.current || 0)) return;

         if (ignoreNextPopRef.current) {
            ignoreNextPopRef.current = false;
            return;
         }

         const hasPopup = Boolean(getCurrentPopup());
         const hasSub = Boolean(getCurrentSubPopup());

         if (!hasPopup && !hasSub) return;

         const popupNow = getCurrentPopup();
         const popupType = popupNow?.type;
         const popupId = String(popupNow?.id ?? popupHistoryIdRef.current ?? "").trim();
         const stateNow =
            (typeof window !== "undefined" ? window.history.state : null) || {};
         if (
            hasPopup &&
            stateNow?.[POPUP_HISTORY_MARK] &&
            String(stateNow?.popupHistoryId ?? "") === popupId
         ) {
            return;
         }

         // subpopup-urile își gestionează singure istoricul
         if (hasSub) {
            return;
         }

         if (!confirmPopupClose(popupType)) {
            armBackGuard(popupId);
            return;
         }

         popupHistoryIdRef.current = null;
         closePopupStore();
         backArmedRef.current = false;
      };

      window.addEventListener("popstate", onPopState);
      return () => window.removeEventListener("popstate", onPopState);
   }, [armBackGuard, confirmPopupClose]);

   // OPEN/CLOSE driven de storePopup
   useEffect(() => {
      clearTimers();
      seqRef.current += 1;
      const seq = seqRef.current;

      if (storePopup) {
         // ===== OPEN =====
         const disableMotionNow = shouldDisablePopupMotion(storePopup);
         motionOffRef.current = disableMotionNow;
         popupOpenedAtTsRef.current = Date.now();
         setShownPopup(storePopup);
         popupHistoryIdRef.current = String(storePopup?.id ?? "");

         // ✅ important: armăm back-guard aici
         armBackGuard(storePopup?.id);

         if (disableMotionNow) {
            setIsOpen(true);
         } else {
            setIsOpen(false);
            raf1Ref.current = requestAnimationFrame(() => {
               raf2Ref.current = requestAnimationFrame(() => {
                  if (seqRef.current !== seq) return;
                  setIsOpen(true);
               });
            });
         }

         return;
      }

      // ===== CLOSE =====
      const disableMotionNow = motionOffRef.current;
      motionOffRef.current = false;
      setIsOpen(false);
      if (disableMotionNow) setShownPopup(null);
      else scheduleClearContent(seq);

      // ✅ popup închis -> considerăm guard dezarmat
      backArmedRef.current = false;
      popupHistoryIdRef.current = null;
   }, [storePopup, clearTimers, scheduleClearContent, armBackGuard]);

   // close handler (click X / overlay)
   const handleClose = useCallback((options = {}) => {
      // dacă ai subpopup -> închide subpopup și gata (nu umblăm la history)
      if (getCurrentSubPopup()) {
         triggerHapticFeedback();
         requestCloseSubPopup();
         return;
      }

      const popupType = getCurrentPopup()?.type;
      const popupId = String(
         getCurrentPopup()?.id ?? popupHistoryIdRef.current ?? "",
      ).trim();
      if (!confirmPopupClose(popupType, options)) return;

      triggerHapticFeedback();
      closePopupStore();

      // ✅ scoate entry-ul dummy ca să nu fie nevoie de încă un back după
      if (typeof window !== "undefined" && isMobilePopupViewport()) {
         const st = window.history.state;
         if (
            st &&
            st[POPUP_HISTORY_MARK] &&
            String(st?.popupHistoryId ?? "") === popupId
         ) {
            ignoreNextPopRef.current = true;
            ignorePopUntilTsRef.current = Date.now() + 900;
            window.history.back();
         }
      }

      backArmedRef.current = false;
      popupHistoryIdRef.current = null;
   }, [confirmPopupClose]);

   // content motion toggle (panel motion rămâne mereu ON)
   const contentMotionOn = useMemo(() => {
      const t = shownPopup?.type;
      if (!t) return true;
      if (shouldDisablePopupMotion(shownPopup)) return false;
      return !NO_CONTENT_MOTION_TYPES.has(t);
   }, [shownPopup]);

   const panelMotionOn = useMemo(
      () => !shouldDisablePopupMotion(shownPopup),
      [shownPopup],
   );

   const content = useMemo(() => {
      const type = shownPopup?.type;
      const props = shownPopup?.props || {};

      switch (type) {
         case "studentDetails":
            return (
               <StudentInfo
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "studentProfile":
            return <StudentSelfProfilePopup key={shownPopup?.id} {...props} />;
         case "eventInfo":
            return <EventInfoPopup key={shownPopup?.id} {...props} />;
         case "studentReservations":
            return <StudentReservationsPopup key={shownPopup?.id} {...props} />;
         case "ultraSlotHistory":
            return <UltraSlotHistoryPopup key={shownPopup?.id} {...props} />;
         case "instructorStudents":
            return <InstructorStudentsPopup key={shownPopup?.id} {...props} />;
         case "reservationEdit":
            return (
               <EditReservation
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "createRezervation":
            return (
               <CreateRezervation
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "practicalExamSlot":
            return (
               <PracticalExamSlotPopup
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "practicalExamGenerateSlots":
            return (
               <PracticalExamGenerateSlotsPopup
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "practicalExamBookStudent":
            return (
               <PracticalExamBookStudentPopup
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "instructorSubstitutions":
            return (
               <InstructorSubstitutionsPopup
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "instructorOrderEditor":
            return (
               <InstructorOrderEditorPopup
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "studentMandatoryRatings":
            return (
               <StudentMandatoryRatingsPopup key={shownPopup?.id} {...props} />
            );
         case "adminReviews":
            return <AdminReviewsPopup key={shownPopup?.id} {...props} />;
         case "notifications":
            return <NotificationsPopup key={shownPopup?.id} {...props} />;
         case "appearanceSettings":
            return <AppearanceSettingsPopup key={shownPopup?.id} {...props} />;
         case "homeWidgetGallery":
            return <HomeWidgetGalleryPopup key={shownPopup?.id} {...props} />;
         case "staffChat":
            return <StaffChatPopup key={shownPopup?.id} {...props} />;
         case "sendVideo":
            return (
               <SendRecommendedVideoPopup key={shownPopup?.id} {...props} />
            );
         case "practicalExamBooking":
            return (
               <PracticalExamBooking
                  key={shownPopup?.id}
                  {...props}
                  variant="popup"
               />
            );
         case "instructorDetails":
            return <InstructorProfileUI key={shownPopup?.id} {...props} />;
         case "managerAdminDetails":
            return <ManagerAdminProfileUI key={shownPopup?.id} {...props} />;
         case "professorDetails":
            return <ProfessorProfileUI key={shownPopup?.id} {...props} />;
         case "staffDetails":
            {
               const role = String(
                  props?.user?.role ?? props?.instructor?.role ?? "",
               )
                  .trim()
                  .toUpperCase();

               if (role === "INSTRUCTOR") {
                  const instructor = props?.instructor || props?.user || null;
                  return (
                     <InstructorProfileUI
                        key={shownPopup?.id}
                        {...props}
                        instructor={instructor}
                     />
                  );
               }

               if (role === "PROFESSOR") {
                  return <ProfessorProfileUI key={shownPopup?.id} {...props} />;
               }

               return <ManagerAdminProfileUI key={shownPopup?.id} {...props} />;
            }
         case "personalCreate":
            return <PersonalCreateUI key={shownPopup?.id} {...props} />;
         case "studentEnroll":
            return <StudentEnrollPopup key={shownPopup?.id} {...props} />;
         case "groupEdit":
            return (
               <GroupEditPopup
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "studentVideoLibrary":
            return <StudentVideoLibraryPopup key={shownPopup?.id} {...props} />;
         case "questionCategories":
            return (
               <QuestionCategoriesPopup
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "startExam":
            return (
               <StudentsMultiSelectPopup
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
               />
            );
         case "sAddProg":
            return (
               <SAddProgAutoAssign
                  key={shownPopup?.id}
                  {...props}
                  onClose={handleClose}
                  registerCloseInterceptor={registerCloseInterceptor}
               />
            );
         default:
            return null;
      }
   }, [shownPopup, handleClose, registerCloseInterceptor]);

   const hasContent = Boolean(shownPopup);

   useEffect(() => {
      if (typeof document === "undefined") return undefined;

      const syncPortalHost = () => {
         setPortalHost(resolvePopupUIPortalHost());
      };

      syncPortalHost();
      document.addEventListener("fullscreenchange", syncPortalHost);
      document.addEventListener("webkitfullscreenchange", syncPortalHost);

      return () => {
         document.removeEventListener("fullscreenchange", syncPortalHost);
         document.removeEventListener("webkitfullscreenchange", syncPortalHost);
      };
   }, []);

   useEffect(() => {
      if (typeof window === "undefined") return;

      const isPhone = window.matchMedia("(max-width: 860px)").matches;
      const shouldLockScroll = isPhone && (isOpen || hasContent);

      document.body.classList.toggle("popupui-open", shouldLockScroll);
      document.documentElement.classList.toggle("popupui-open", shouldLockScroll);

      return () => {
         document.body.classList.remove("popupui-open");
         document.documentElement.classList.remove("popupui-open");
      };
   }, [isOpen, hasContent]);

   const tree = (
      <>
         {(isOpen || hasContent) && (
            <div
               className={`popupPanelUI__overlay ${isOpen ? "is-open" : ""}`}
               data-panel-motion={panelMotionOn ? "on" : "off"}
               style={{ pointerEvents: isOpen ? "auto" : "none" }}
               onPointerDown={(e) => {
                  if (typeof e.button === "number" && e.button !== 0) return;
                  if (
                     isMandatoryPopupType(getCurrentPopup()?.type) ||
                     isMandatoryPopupType(shownPopup?.type)
                  ) {
                     return;
                  }
                  if (Date.now() - Number(popupOpenedAtTsRef.current || 0) < 180) {
                     return;
                  }
                  handleClose();
               }}
            />
         )}

         <div
            ref={panelRef}
            className={`popupPanelUI ${isOpen ? "is-open" : ""}`}
            data-panel-motion={panelMotionOn ? "on" : "off"}
            data-content-motion={contentMotionOn ? "on" : "off"}
            data-popup-type={shownPopup?.type || ""}
            aria-hidden={!isOpen}
         >
            {hasContent && (
               <ReactSVG
                  onClick={
                     isMandatoryPopupType(shownPopup?.type)
                        ? undefined
                        : handleClose
                  }
                  className={`popupPanelUI__close react-icon rotate45 ${
                     isMandatoryPopupType(shownPopup?.type)
                        ? "is-disabled"
                        : ""
                  }`}
                  src={addIcon}
                  aria-hidden={
                     isMandatoryPopupType(shownPopup?.type) ? "true" : undefined
                  }
               />
            )}

            <div className="popupPanelUI__inner">
               <div className="popupPanelUI__content">{content}</div>
            </div>
         </div>
      </>
   );

   return portalHost ? createPortal(tree, portalHost) : tree;
}
